import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, NavigationEnd, NavigationStart, Router } from '@angular/router';
import { filter } from 'rxjs';
import { PageTitleService } from '../../services/page-title.service';
import { AuthService } from '../../services/auth.services';

@Component({
  selector: 'app-title',
  templateUrl: './title.html',
  styleUrl: './title.scss',
  standalone: true
})
export class Title implements OnInit {
  private router = inject(Router);
  private activatedRoute = inject(ActivatedRoute);
  private pageTitle = inject(PageTitleService);
  private authService = inject(AuthService);

  private readonly tituloDaRota = signal<string | undefined>(undefined);
  private readonly overrideTitle = toSignal(this.pageTitle.override$, { initialValue: null });

  /** O título dinâmico da tela vence o override e o `title` da rota (PageTitleService). */
  readonly title = computed(
    () => this.pageTitle.titulo() ?? this.overrideTitle() ?? this.tituloDaRota(),
  );

  ngOnInit(): void {
    // Descartado no início da navegação — antes de a próxima tela ser criada —, para que
    // nenhuma página herde o título dinâmico da anterior nem perca o que acabou de definir.
    this.router.events
      .pipe(filter(event => event instanceof NavigationStart))
      .subscribe(() => this.pageTitle.limpar());

    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => {
        this.tituloDaRota.set(this.resolveTitle(this.getDeepestRoute()));
      });

    this.tituloDaRota.set(this.resolveTitle(this.getDeepestRoute()));
  }

  private getDeepestRoute(): ActivatedRoute {
    let currentRoute = this.activatedRoute;

    while (currentRoute.firstChild) {
      currentRoute = currentRoute.firstChild;
    }

    return currentRoute;
  }

  private resolveTitle(route: ActivatedRoute): string | undefined {
    const titleByRole = route.snapshot.data['titleByRole'] as
      | {role: string; title: string}[]
      | undefined;

    const byRole = titleByRole?.find(config =>
      this.authService.hasRole(config.role)
    );

    return byRole?.title ?? route.snapshot.data['title'];
  }
}
